const { getSmoothStepPath } = require('react-flow-renderer');

const leftRight = ['left', 'right'];
const topBottom = ['bottom', 'top'];

interface Connection
{
    id: string
    position: string
    x: number
    y: number
}
export function getCorner(source: Connection, target: Connection, offset = 30)
{
    let x, y;
    if (topBottom.includes(source.position)) y = source.y <= target.y ? source.y - offset : source.y + offset;
    else y = source.y <= target.y ? target.y + offset : target.y - offset;

    if (leftRight.includes(source.position)) x = source.x <= target.x ? source.x - offset : source.x + offset;
    else x = source.x <= target.x ? target.x + offset : target.x - offset;

    return [x, y];
}

function getLoopPath(source: Connection, target: Connection, node: NodeDimensions, margin: number, radius: number)
{
    const targetLeftMargin = target.x - margin;

    let path = `M ${source.x},${source.y} L ${source.x + margin - radius},${source.y} `;

    path += `Q ${source.x + margin - radius},${source.y} ${source.x + margin},${source.y + radius}`
    path += `L ${source.x + margin},${source.y + node.height * 0.5 + margin - radius} `
    path += `Q ${source.x + margin},${source.y + node.height * 0.5 + margin - radius} ${source.x + margin - radius},${source.y + node.height * 0.5 + margin}`
    path += `L ${targetLeftMargin + radius},${source.y + node.height * 0.5 + margin} `
    path += `Q ${targetLeftMargin + radius},${source.y + node.height * 0.5 + margin} ${targetLeftMargin},${source.y + node.height * 0.5 + margin - radius}`
    path += `L ${targetLeftMargin},${target.y + radius} `
    path += `Q ${targetLeftMargin},${target.y + radius} ${targetLeftMargin + radius},${target.y}`
    path += `L ${target.x},${target.y} `
    return path;
}

export function getMiddle(source: Connection, target: Connection, offset = 75)
{
    let x, y;

    if (topBottom.includes(source.position))
    {
        x = (source.x + target.x) / 2;
        y =
            source.position === 'top'
                ? Math.min(source.y, target.y) - offset
                : Math.max(source.y, source.y) + offset;
    } else
    {
        x =
            source.position === 'left'
                ? Math.min(source.x, target.x) - offset
                : Math.max(source.x, target.x) + offset;
        y = (source.y + target.y) / 2;
    }

    return [x, y];
}

interface NodeDimensions
{
    height: number
    width: number
}
function getPathLeft(source: Connection, target: Connection, node: NodeDimensions,
    sourcePosition: number, margin: number, radius: number)
{
    let path = `M ${source.x},${source.y} L ${source.x + margin - radius},${source.y} `;
    const halfHeight = node.height * 0.5;
    const sourceRightMargin = source.x + margin;
    const sourceLeftMargin = source.x - node.width - margin;
    const sourceLowerMargin = source.y + node.height - sourcePosition + margin;
    const sourceUpperMargin = source.y - sourcePosition - margin;
    const targetLeftMargin = target.x - margin;
    const targetLowerMargin = target.y + halfHeight + margin;
    const targetUpperMargin = target.y - halfHeight - margin;

    if (sourceUpperMargin > targetLowerMargin || sourceLowerMargin < targetUpperMargin)
    {
        // Simple four junction path around source
        const elbowDelta = sourceUpperMargin > targetLowerMargin ? 1 : -1;
        const sourceMarginY = sourceUpperMargin > targetLowerMargin ? sourceUpperMargin : sourceLowerMargin;
        const elbowRadius = radius * elbowDelta;

        path += `Q ${sourceRightMargin - radius},${source.y} ${sourceRightMargin},${source.y - elbowRadius}`
        path += `L ${sourceRightMargin},${sourceMarginY + elbowRadius} `
        path += `Q ${sourceRightMargin},${sourceMarginY + elbowRadius} ${sourceRightMargin - radius},${sourceMarginY}`
        path += `L ${targetLeftMargin + radius},${sourceMarginY} `
        path += `Q ${targetLeftMargin + radius},${sourceMarginY} ${targetLeftMargin},${sourceMarginY - elbowRadius}`
        path += `L ${targetLeftMargin},${target.y + elbowRadius} `
        path += `Q ${targetLeftMargin},${target.y + elbowRadius} ${targetLeftMargin + radius},${target.y}`
        path += `L ${target.x},${target.y} `
    } else
    {
        // Six junction elbow bath under source
        const sourceMarginY = source.y > target.y ? sourceLowerMargin : sourceUpperMargin;
        const targetMargin = source.y > target.y ? targetLowerMargin : targetUpperMargin;
        const elbowDelta = source.y > target.y ? 1 : -1;
        const elbowRadius = radius * elbowDelta;
        const midDelta = sourceMarginY > targetMargin ? -1 : 1;
        const midSize = Math.min(Math.abs(sourceMarginY - targetMargin), radius);
        const midRadius = midSize * midDelta;

        path += `Q ${sourceRightMargin - radius},${source.y} ${sourceRightMargin},${source.y + elbowRadius}`
        path += `L ${sourceRightMargin},${sourceMarginY - elbowRadius} `
        path += `Q ${sourceRightMargin},${sourceMarginY - elbowRadius} ${sourceRightMargin - radius},${sourceMarginY}`
        path += `L ${sourceLeftMargin + midSize},${sourceMarginY} `;
        path += `Q ${sourceLeftMargin + midSize},${sourceMarginY} ${sourceLeftMargin},${sourceMarginY + midRadius}`
        path += `L ${sourceLeftMargin},${targetMargin - midRadius} `;
        path += `Q ${sourceLeftMargin},${targetMargin - midRadius} ${sourceLeftMargin - midSize},${targetMargin}`
        path += `L ${targetLeftMargin + radius},${targetMargin} `
        path += `Q ${targetLeftMargin + radius},${targetMargin} ${targetLeftMargin},${targetMargin - elbowRadius}`
        path += `L ${targetLeftMargin},${target.y + elbowRadius} `
        path += `Q ${targetLeftMargin},${target.y + elbowRadius} ${targetLeftMargin + radius},${target.y}`
        path += `L ${target.x},${target.y} `
    }
    return path;
}

function lerp(a: number, b: number, t: number): number
{
    return (t * (b - a)) + a;
}

export function getSmartPath(source: Connection, target: Connection, node: NodeDimensions, sourcePosition: number, transitionIndex: number, transitionCount: number,
    margin = 40, radius = 3)
{
    if (source.id === target.id)
    {
        return getLoopPath(source, target, node, margin, radius);
    }
    if (source.x > target.x)
    {
        return getPathLeft(source, target, node, sourcePosition, margin, radius);
    }

    const diff = Math.abs(target.x - source.x);
    const centerX = source.y > target.y ?
        lerp(source.x + diff / 8, target.x - diff / 8, (transitionIndex + 0.5) / transitionCount) :
        lerp(target.x - diff / 8, source.x + diff / 8, (transitionIndex + 0.5) / transitionCount);

    return getSmoothStepPath({
        sourceX: source.x,
        sourceY: source.y,
        sourcePosition: source.position,
        targetX: target.x,
        targetY: target.y,
        targetPosition: target.position,
        centerX: transitionCount > 1 ? centerX : undefined
    });
}

export function getLabelPosition(source: Connection, target: Connection, node: NodeDimensions,
    size: { height: number, width: number }, margin: number): { x: number, y: number }
{
    const position = {
        x: source.x * 0.75 + target.x * 0.25 - size.width * 0.5,
        y: source.y * 0.75 + target.y * 0.25 - size.height * 0.5
    };

    if (source.id === target.id)
    {
        position.x = source.x * 0.5 + target.x * 0.5 - size.width * 0.5;
        position.y += node.height * 0.5 + margin;
    } else if (source.x > target.x)
    {
        position.x = source.x + margin / 2;
        position.y = source.y + (target.y > source.y ? margin * 0.5 : - size.height + margin * - 0.5);
    }
    return position;
}